/**
 * Variant-axis form types - PR-E2b.
 *
 * These types describe Pokémon species that have more than one *form*
 * (Vivillon patterns, Furfrou trims, Unown letters, Rattata regional
 * variants, etc.) so a single species id can resolve to multiple
 * dex entries, each carrying its own sprite + display name.
 *
 * Data lives in two generated files committed to the repo:
 *   - client/src/lib/generated/pokemonForms.generated.ts
 *   - client/src/lib/generated/pokemonFormSprites.generated.json
 *
 * Both are produced by `scripts/generate-pokemon-forms.ts`, which
 * hits the PokéAPI GraphQL endpoint at build time and writes static
 * output. The runtime app NEVER calls PokéAPI directly for form
 * metadata.
 *
 * Sprite URLs point at our own R2 mirror (sprites.nexusdex.ai) — see
 * `client/src/lib/spriteCdn.ts`, same as `pokemonArtwork.ts` uses for
 * caught-cell colour art. This closes the old "PR-Fxx sprite mirror"
 * TODO: both the official artwork and these form sprites are mirrored
 * in one shot by `scripts/sync-sprites-to-r2.ts`, so we honour
 * PokéAPI's fair-use stance instead of hotlinking them at runtime.
 */
import { SPRITE_CDN_POKEAPI } from "@/lib/spriteCdn";
import { isReleasedAtLocal, isReleaseMomentPast } from "@/lib/releaseAtLocal";

/**
 * `formId` is a stable string identifier for a single form of a
 * species. We use the PokéAPI `pokemon_v2_pokemonform.name` slug
 * directly (e.g. `vivillon-meadow`, `furfrou-pharaoh`, `unown-a`,
 * `rattata-alola`) because it is both globally unique across all
 * species and human readable in URLs / logs.
 *
 * For the default form of a species the slug equals the species name
 * (e.g. `pikachu`); we still emit a FormVariant for the default so the
 * sub-dex screen can render it alongside its alt forms.
 */
export type FormId = string;

/**
 * What KIND of alternate entry a form is.
 *
 *   form    — a real alternate form: regional (Alolan, Galarian), pattern
 *             (Vivillon, Unown, Spinda), or mechanical (Deoxys, Giratina).
 *             The default when `kind` is absent, which is every PokéAPI-derived
 *             form.
 *   costume — a Pokémon GO event costume: the same Pokémon wearing something
 *             (party hats, Ash caps, GO Fest / GO Tour / WCS sets). Main-series
 *             PokéAPI does not model these at all; they come from the GO
 *             GAME_MASTER and their art is the PokeMiners GO asset mirror.
 *
 * Kept as a field rather than inferred from the slug because the two are not
 * separable by name — `pikachu-original-cap` reads like a costume and comes
 * from PokéAPI, while `pikachu-kariyushi` reads like a form and is a costume.
 * The dex's Forms filter and the detail page both split on this.
 */
export type FormKind = "form" | "costume";

/**
 * A single form of a species: the unit a user can mark caught
 * independently on a sub-dex.
 */
export interface FormVariant {
  /** Stable slug - see `FormId`. */
  formId: FormId;
  /** PokéAPI form name (matches `formId` for now; kept for forward compat). */
  name: string;
  /** UI label, e.g. `Meadow Pattern`, `Pharaoh Trim`, `Alolan`. */
  displayName: string;
  /** Front-facing sprite URL (non-shiny). */
  spritePath: string;
  /** Front-facing shiny sprite URL, or null if PokéAPI has no shiny art for this form. */
  shinySpritePath: string | null;
  /** True for the species' default form (e.g. Vivillon-Meadow is NOT default; Pikachu is). */
  isDefault: boolean;
  /**
   * Form vs GO event costume. Absent means `"form"` — every PokéAPI-derived
   * entry — so only costumes carry the field and the generated file stays
   * readable. See `FormKind`.
   */
  kind?: FormKind;
  /**
   * PR-E2b.1: PoGo release status. Absent or `true` means the form is
   * obtainable in-game today; `false` means the dex slot exists but no
   * way to catch it yet (currently only Spinda patterns 10..20). The
   * sub-dex UI renders unreleased forms locked/silhouette.
   */
  isReleased?: boolean;
  /**
   * Optional NAIVE local wall-clock release moment ("2026-09-05T10:00", no
   * zone suffix), for event content shipped in the bundle ahead of its event
   * (a new costume Pikachu bundled before the event weekend). Resolved on the
   * DEVICE clock because GO events start at local time in every player's zone
   * — same field and semantics as MegaRosterOverride.releaseAtLocal, shared
   * gate in lib/releaseAtLocal.ts. Until the moment passes the form renders
   * locked, exactly like `isReleased: false`. Absent = released immediately.
   */
  releaseAtLocal?: string;
  /**
   * 2026-09-07: has GO released this form's SHINY? `shinySpritePath` only
   * says the asset mirror carries shiny art, and PokeMiners exports art for
   * shinies GO has never shipped (Armored Mewtwo). Absent = follows the
   * species' shiny roster; `false` = never offer a Shiny tile for this form.
   */
  shinyReleased?: boolean;
  /**
   * 2026-09-22: naive device-local moment the form's SHINY debuts
   * ("2026-10-04T00:00"), for a costume the owner wants in Nexus Dex ahead of
   * its event while the Shiny tile waits for the debut (Astronaut Pikachu).
   * Unlike `releaseAtLocal` this is the EXACT moment, no lookahead: a shiny
   * that is not out yet cannot be chased. Absent = no date gate.
   */
  shinyReleaseAtLocal?: string;
}

/**
 * Can a Shiny of this form be chased at all: it needs shiny art AND GO must
 * have released the shiny (see `shinyReleased` and `shinyReleaseAtLocal`).
 * The species-level roster is the caller's second check.
 */
export function isFormShinyReleased(form: FormVariant, now: Date = new Date()): boolean {
  return !!form.shinySpritePath && isFormShinyOut(form, now);
}

/**
 * Has GO released this form's Shiny yet, regardless of whether the mirror has
 * shiny art for it: the static `shinyReleased` flag AND the exact-moment
 * `shinyReleaseAtLocal` gate. The Shiny-family grids lock a cell on this so a
 * costume in Nexus Dex ahead of its event cannot be ticked Shiny before the
 * debut (a form without shiny art draws its plain art there, as before).
 */
export function isFormShinyOut(form: FormVariant, now: Date = new Date()): boolean {
  return form.shinyReleased !== false && isReleaseMomentPast(form.shinyReleaseAtLocal, now);
}

/**
 * Is this form obtainable right now? Combines the static `isReleased` flag
 * with the device-local `releaseAtLocal` date gate. Every surface that renders
 * or counts a form's locked state must go through this — checking the flag
 * alone silently ignores scheduled event releases.
 */
export function isFormVariantReleased(form: FormVariant, now: Date = new Date()): boolean {
  return form.isReleased !== false && isReleasedAtLocal(form.releaseAtLocal, now);
}

/**
 * Grouping of all forms of a single species. Emitted only for species
 * that have >= 2 forms (single-form species do not appear in the
 * generated file).
 *
 * Spinda is a special case: PokéAPI collapses its sprite variations to
 * a single form, but PoGo's dex enumerates 20 patterns (9 currently
 * released). PR-E2b.1 lifts Spinda into this axis via a manual
 * override (see `scripts/manualFormOverrides.ts`).
 */
export interface FormGroup {
  /** PokéAPI species id (== national dex id for the base species). */
  speciesId: number;
  /** National dex id of the base species (alias of `speciesId`; kept for clarity at call sites). */
  baseDexId: number;
  /** Species name slug (e.g. `vivillon`, `furfrou`, `unown`, `rattata`). */
  speciesName: string;
  /** All forms of this species, in PokéAPI ordering. */
  forms: FormVariant[];
}

/**
 * Helper: build the canonical sprite URL for a form id on our own mirror.
 * Mirrors the URL shape used in `client/src/lib/pokemonArtwork.ts` so both
 * data sources stay consistent. Used by the generator; can also be used at
 * runtime if we ever need to fall back to ID-based URL construction.
 */
export const POKEAPI_FORM_SPRITE_BASE = SPRITE_CDN_POKEAPI;

export function formSpriteUrl(pokemonId: number, opts?: { shiny?: boolean }): string {
  const variant = opts?.shiny ? "shiny" : "";
  return variant
    ? `${POKEAPI_FORM_SPRITE_BASE}/${variant}/${pokemonId}.png`
    : `${POKEAPI_FORM_SPRITE_BASE}/${pokemonId}.png`;
}
